import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

const PLACEHOLDER_IMG = "https://placehold.co/200?text=Nessuna%5CnImmagine&font=rubik";

const shellSx = {
  borderRadius: "12px",
  backgroundColor: "#ffffff",
  border: "1px solid rgba(0,0,0,0.06)",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  px: 2,
};

function TopClickedProductsChartCard({ item, maxClicks, compact, onActivate }) {
  const clicks = Number(item.clickCount) || 0;
  const max = maxClicks > 0 ? maxClicks : 1;
  const widthPct = Math.round((clicks / max) * 100);
  const primaryImage = item.imageUrls?.[0] ?? PLACEHOLDER_IMG;
  const label = item.label ?? "";

  const barTrack = (
    <Box
      sx={{
        flex: 1,
        minWidth: 48,
        height: 12,
        backgroundColor: "#f1f5f9",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${widthPct}%`,
          backgroundColor: "#0b3d87",
          borderRadius: 1,
        }}
      />
    </Box>
  );

  const interactiveProps = onActivate
    ? {
        role: "button",
        tabIndex: 0,
        onClick: onActivate,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        },
      }
    : {};

  if (compact) {
    return (
      <Box
        {...interactiveProps}
        sx={{
          ...shellSx,
          py: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1.5,
          minHeight: 56,
          cursor: onActivate ? "pointer" : "default",
        }}
      >
        <Box
          component="img"
          src={primaryImage}
          alt=""
          sx={{
            width: 48,
            height: 48,
            borderRadius: "10px",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
        {barTrack}
        <Typography
          sx={{
            fontFamily: "Rubik",
            fontWeight: 700,
            color: "#0b3d87",
            fontSize: 15,
            minWidth: 40,
            textAlign: "right",
            flexShrink: 0,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {clicks}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...shellSx,
        py: 1.25,
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 1.5,
        cursor: "default",
      }}
    >
      <Box
        component="img"
        src={primaryImage}
        alt=""
        sx={{
          width: 48,
          height: 48,
          borderRadius: "10px",
          objectFit: "cover",
          flexShrink: 0,
          mt: 0.25,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: "Rubik",
            fontWeight: 700,
            color: "#0b3d87",
            fontSize: 15,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
          title={label}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          {barTrack}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              flexShrink: 0,
              minWidth: 56,
            }}
          >
            <Typography sx={{ fontFamily: "Rubik", color: "#808080", fontSize: 12, lineHeight: 1.2 }}>Click</Typography>
            <Typography
              sx={{
                fontFamily: "Rubik",
                fontWeight: "bold",
                color: "#2e7d32",
                fontSize: 16,
                lineHeight: 1.2,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {clicks}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

TopClickedProductsChartCard.propTypes = {
  item: PropTypes.shape({
    label: PropTypes.string,
    clickCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    imageUrls: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  maxClicks: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  onActivate: PropTypes.func,
};

export default TopClickedProductsChartCard;
